/**
 * 富文本白名单净化（**零依赖**）——与后端 t17 的 DeliveryHub.Common 净化器**同口径**的最小前端实现。
 *
 * 分工：服务端仍是**权威**（入库前净化、读回即净化后值）；本层负责「渲染面与提交面都不把未过滤的
 * 字符串交出去」——v-html 只吃本函数的返回值，提交前也先过一遍，两侧同规则才不会出现
 * 「前端看着干净、落库又被改一刀」或反过来的口径漂移。
 *
 * 规则（逐条对应后端）：
 *   1. 允许标签：p br strong b em i u s del h1 h2 h3 h4 ul ol li blockquote a code pre span（无属性）；
 *   2. a 只保留 href：只收 http/https、⛔ 不收带 userinfo 的地址；保留即补 rel="noreferrer noopener" target="_blank"；
 *   3. script/style **连内容**丢弃（未闭合的尾巴也丢）；
 *   4. 未知标签**只解包**（丢标签、留文本）；
 *   5. 一律删除 on*／style／class／id／src 等属性；HTML 注释整段丢弃；
 *   6. **幂等**：sanitizeRichHtml(sanitizeRichHtml(x)) === sanitizeRichHtml(x)。
 *
 * ⚠ 这里⛔ 引第三方净化依赖（本单红线）：实现刻意只做「产出」不做「信任」——
 * 即便本层被绕过，后端仍会再净化一次。
 */

/** 允许标签清单（顺序仅作文档；与后端白名单同集） */
export const RICH_TEXT_ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'h1',
  'h2',
  'h3',
  'h4',
  'ul',
  'ol',
  'li',
  'blockquote',
  'a',
  'code',
  'pre',
  'span',
] as const;

/** 后端 contentHtml 上限（字符数），前端就地拦一次，避免白跑一趟 422 */
export const RICH_TEXT_MAX_LENGTH = 131072;

const ALLOWED = new Set<string>(RICH_TEXT_ALLOWED_TAGS as readonly string[]);

/** 属性取值：双引号／单引号／裸值三形；⛔ 不做实体解码（来回一趟才不会把 &amp; 越滚越多） */
function attrValue(attrs: string, name: string): string | null {
  const pattern = new RegExp(name + '\\s*=\\s*(?:"([^"]*)"|\'([^\']*)\'|([^\\s>]+))', 'i');
  const hit = pattern.exec(attrs);
  if (!hit) return null;
  return hit[1] ?? hit[2] ?? hit[3] ?? '';
}

/** href 白名单：只收 http/https；⛔ userinfo（user:pass@host）一律不收 */
function safeHref(raw: string): string | null {
  const value = raw.trim();
  if (!/^https?:\/\//i.test(value)) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.username || url.password) return null;
    return value;
  } catch {
    return null;
  }
}

/** 白名单净化。入参 null/undefined ⇒ 空串（调用方据此决定「提交 null 还是空串」）。 */
export function sanitizeRichHtml(input: string | null | undefined): string {
  if (input === null || input === undefined) return '';
  let html = String(input);

  // 1) 注释整段丢弃
  html = html.replace(/<!--[\s\S]*?-->/g, '');
  // 2) script/style 连内容丢弃；再兜未闭合的尾巴与孤立标签
  html = html.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '');
  html = html.replace(/<(script|style)\b[\s\S]*$/gi, '');
  html = html.replace(/<\/?(script|style)\b[^>]*>/gi, '');

  // 3) 逐枚标签过白名单（属性里的 > 由引号分支兜住；开闭由 match 前缀判）
  return html
    .replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)((?:"[^"]*"|'[^']*'|[^>])*)\/?>/g, (match: string, rawName: string, rawAttrs: string) => {
      const name = String(rawName).toLowerCase();
      // 未知标签只解包：丢标签、留文本
      if (!ALLOWED.has(name)) return '';
      if (match.startsWith('</')) return '</' + name + '>';
      if (name === 'a') {
        const href = attrValue(String(rawAttrs), 'href');
        const safe = href === null ? null : safeHref(href);
        if (!safe) return '<a>';
        return '<a href="' + safe.replace(/"/g, '&quot;') + '" rel="noreferrer noopener" target="_blank">';
      }
      return '<' + name + '>';
    })
    .trim();
}

/**
 * 渲染面唯一入口：⛔ 别处再写一遍 v-html 的取值（视图与组件里 v-html 只许绑本函数的结果）。
 * 与 sanitizeRichHtml 的区别只在「空值语义」：这里给的是可直接进 v-html 的字符串。
 */
export function safeRichHtml(input: string | null | undefined): string {
  return sanitizeRichHtml(input);
}

/** 净化后是否为空（提交时据此把空串落成 null，与后端「净化后为空则存 null」同口径） */
export function richTextOrNull(input: string | null | undefined): string | null {
  const clean = sanitizeRichHtml(input);
  return clean === '' ? null : clean;
}
